import React from 'react'
import { Input as InputComponent } from '@/components/ui/input'
import { formatCurrencyAutoLocale } from '@/utils/tools'
import {
    ADVANTAGE_AUDIENCE_NOTE,
    FAIR_SPLIT_NOTE,
    PLACEMENTS_NOTE,
    campaignBudgetProblem,
} from '@/utils/advantage-leads'

const Input = InputComponent as React.FC<React.InputHTMLAttributes<HTMLInputElement>>

interface AdvantageLeadsBudgetProps {
    /** Daily campaign budget as typed (major units). */
    campaignBudget: string
    onChange: (value: string) => void
    currency?: string
    /** Minimum daily budget of an ad set of the ad account (minor units). */
    minDailyBudget: number
    /** Audiences (ad sets) the campaign budget is shared across. */
    adSetCount: number
    /** Percentage of the campaign budget each AI audience is asked to get at least; 0 for none. */
    minSpendShare?: number
}

/** Message for a campaign budget the server would refuse, or null. */
export const describeCampaignBudgetProblem = (
    amount: string,
    adSetCount: number,
    minDailyBudget: number,
    currency?: string,
): string | null => {
    const problem = campaignBudgetProblem(amount, adSetCount, minDailyBudget)
    if (!problem) return null
    if (typeof problem === 'string') return problem
    const count = Math.max(adSetCount, 1)
    return `The campaign budget must be at least ${formatCurrencyAutoLocale({
        amount: problem.minimum / 100,
        currency,
    })} a day, the minimum daily budget of ${count} ${count === 1 ? 'audience' : 'audiences'}.`
}

/**
 * One daily budget for the whole campaign (Advantage+ leads): Meta shares it across the audiences and chooses the
 * placements, so no audience gets a budget of its own.
 */
const AdvantageLeadsBudget: React.FC<AdvantageLeadsBudgetProps> = ({
    campaignBudget,
    onChange,
    currency,
    minDailyBudget,
    adSetCount,
    minSpendShare = 0,
}) => {
    const problem = describeCampaignBudgetProblem(campaignBudget, adSetCount, minDailyBudget, currency)

    return (
        <div className="tw:px-6 tw:flex tw:flex-col tw:gap-3" data-testid="advantage-leads-budget">
            <label htmlFor="advantage-leads-budget" className="tw:text-sm tw:font-semibold">
                Daily campaign budget
            </label>
            <Input
                id="advantage-leads-budget"
                type="number"
                min="0"
                step="0.01"
                value={campaignBudget}
                onChange={(event) => onChange(event.target.value)}
                className="tw:max-w-[200px]"
            />
            {problem && (
                <span className="tw:text-sm tw:text-yellow-300" role="status">
                    {problem}
                </span>
            )}
            <p className="tw:text-sm tw:text-gray-200">
                {`Meta shares this budget across the ${adSetCount} ${
                    adSetCount === 1 ? 'audience' : 'audiences'
                } of the campaign; the audiences get no budget of their own.`}
            </p>
            <p className="tw:text-sm tw:text-gray-200" data-testid="advantage-leads-placements">
                {PLACEMENTS_NOTE}
            </p>
            <p className="tw:text-sm tw:text-gray-200">{ADVANTAGE_AUDIENCE_NOTE}</p>
            {minSpendShare > 0 && (
                <p className="tw:text-sm tw:text-gray-200">
                    {`Each AI audience is asked to get at least ${minSpendShare}% of the budget a day; Meta treats this as a target, not a guarantee.`}
                </p>
            )}
            <p className="tw:text-sm tw:text-yellow-300">{FAIR_SPLIT_NOTE}</p>
        </div>
    )
}

export default AdvantageLeadsBudget
